import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { apiFetch } from '../../api';
import { formatFeatureLabel, getFeatureDefinition } from '../../featureLabels';
import { DistributionPlot } from './DistributionPlot';
import { FeatureCardModal } from './FeatureCardModal';
import { SegmentedControl } from './SegmentedControl';


// Patient Normative Results Component
export const PatientNormativeResults = ({ patientZScores }) => {
    const [expandedPatients, setExpandedPatients] = useState({});
    const [expandedRegions, setExpandedRegions] = useState({});
    const [regionDisplayModes, setRegionDisplayModes] = useState({});
    const [selectedModel, setSelectedModel] = useState(null);
    const [currentModalShowZScores, setCurrentModalShowZScores] = useState(true);

    // Debugging
    useEffect(() => {
        console.log("PatientNormativeResults received patientZScores:", patientZScores);

        // Auto-expand the first patient for convenience
        if (patientZScores && patientZScores.length > 0) {
            setExpandedPatients(prev => ({
                ...prev,
                [patientZScores[0].patient_id]: true
            }));
        }
    }, [patientZScores]);

    // Function to toggle patient expansion
    const togglePatient = (patientId) => {
        setExpandedPatients(prev => ({
            ...prev,
            [patientId]: !prev[patientId]
        }));
    };

    // Function to toggle region expansion
    const toggleRegion = (patientId, regionName) => {
        const key = `${patientId}-${regionName}`;
        setExpandedRegions(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Function to toggle display mode (z-scores vs raw values)
    const toggleDisplayMode = (patientId, regionName, value) => {
        const key = `${patientId}-${regionName}`;
        setRegionDisplayModes(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Modal handlers
    const openFeatureModal = useCallback((model, showZScores) => {
        setSelectedModel(model);
        setCurrentModalShowZScores(showZScores);
    }, []);

    const closeFeatureModal = useCallback(() => {
        setSelectedModel(null);
    }, []);

    // Helper function to get display mode
    const getDisplayMode = (patientId, regionName) => {
        const key = `${patientId}-${regionName}`;
        return regionDisplayModes[key] !== undefined ? regionDisplayModes[key] : true; // Default to z-scores
    };

    // Function to group features by region
    const groupFeaturesByRegion = (features) => {
        return features.reduce((acc, feature) => {
            // Use feature name as the key
            const key = feature.feature;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(feature);
            return acc;
        }, {});
    };

    // Check if there's any data
    if (!patientZScores || patientZScores.length === 0) {
        return (
            <div className="empty-message">No patient normative modeling data available</div>
        );
    }

    // Check if any patient has regions data
    const hasRegionsData = patientZScores.some(patient =>
        patient.regions && patient.regions.length > 0
    );

    if (!hasRegionsData) {
        return (
            <div className="empty-message">
                <h3>No results found</h3>
                <p>No brain regions with features were found for the selected patients.</p>
                <p>This could be because:</p>
                <ul>
                    <li>The selected patients don't have electrode data in the database</li>
                    <li>The electrode data for these patients doesn't have feature values</li>
                    <li>No normative models exist for the regions in these patients</li>
                </ul>
                <p>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            apiFetch('/api/check-mni-patients')
                                .then(response => response.json())
                                .then(data => {
                                    console.log('MNI Patient Data Check:', data);
                                    alert('Check details in console. This helps with debugging.');
                                })
                                .catch(err => console.error('Error checking MNI patients:', err));
                        }}
                        style={{
                            color: '#4f46e5',
                            textDecoration: 'underline',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            font: 'inherit'
                        }}
                    >
                        Check MNI Patients Data (Debug)
                    </button>
                </p>
            </div>
        );
    }

    return (
        <div className="patient-normative-results">
            {patientZScores.map(patient => (
                <div key={patient.patient_id} className="patient-section">
                    <div
                        className="patient-header"
                        onClick={() => togglePatient(patient.patient_id)}
                    >
                        <h3>
                            <span className="toggle-icon">
                                {expandedPatients[patient.patient_id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            </span>
                            Patient: {patient.patient_id}
                            <span className="region-count">
                                ({patient.regions.length} brain region{patient.regions.length !== 1 ? 's' : ''})
                            </span>
                        </h3>
                    </div>

                    {expandedPatients[patient.patient_id] && (
                        <div className="patient-regions">
                            {patient.regions.map(region => {
                                const key = `${patient.patient_id}-${region.region_name}`;
                                const isExpanded = expandedRegions[key];
                                const showZScores = getDisplayMode(patient.patient_id, region.region_name);

                                // Extract features from channels - new data structure has z_scores per channel
                                const featuresInRegion = [];
                                if (region.channels) {
                                    region.channels.forEach(channel => {
                                        if (channel.z_scores) {
                                            Object.entries(channel.z_scores).forEach(([featureName, featureData]) => {
                                                featuresInRegion.push({
                                                    feature: featureName,
                                                    channel: channel.channel,
                                                    z_score: featureData.z_score,
                                                    raw_value: featureData.raw_value,
                                                    coordinates: channel.coordinates,
                                                    normative_model: featureData.normative_model
                                                });
                                            });
                                        }
                                    });
                                }

                                // Group features by feature name
                                const groupedFeatures = groupFeaturesByRegion(featuresInRegion);

                                return (
                                    <div key={key} className="normative-group-section">
                                        <div
                                            className="region-header"
                                            onClick={() => toggleRegion(patient.patient_id, region.region_name)}
                                        >
                                            <h3>
                                                <span className="toggle-icon">
                                                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                                </span>
                                                {region.region_name}
                                                <span className="feature-count">({Object.keys(groupedFeatures).length} {Object.keys(groupedFeatures).length === 1 ? 'feature' : 'features'}, {region.channels?.length || 0} {region.channels?.length === 1 ? 'channel' : 'channels'})</span>
                                            </h3>
                                        </div>

                                        {isExpanded && (
                                            <>
                                                <div className="region-controls" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                                    <SegmentedControl
                                                        name={`displayMode-${key}`}
                                                        options={[
                                                            { label: 'Z-Scores', value: true },
                                                            { label: 'Raw Values', value: false }
                                                        ]}
                                                        selectedValue={showZScores}
                                                        onChange={(value) => toggleDisplayMode(patient.patient_id, region.region_name, value)}
                                                    />
                                                </div>

                                                <div className="normative-feature-grid">
                                                    {Object.entries(groupedFeatures).map(([featureName, features]) => {
                                                        // Get the normative model for this feature
                                                        const featureModel = features[0]?.normative_model;
                                                        if (!featureModel) {
                                                            console.warn(`Missing normative_model for feature ${featureName} in region ${region.region_name}`, features[0]);
                                                            return null;
                                                        }

                                                        // Prepare data for the distribution plot
                                                        const model = {
                                                            feature_name: featureName,
                                                            region_name: region.region_name,
                                                            mean: featureModel.mean,
                                                            std: featureModel.std,
                                                            sample_size: region.channels.length,
                                                            min: featureModel.mean - 3 * featureModel.std,
                                                            max: featureModel.mean + 3 * featureModel.std
                                                        };

                                                        // Prepare channel values and z-scores
                                                        const channelValues = [];
                                                        const zScores = [];

                                                        region.channels.forEach(channel => {
                                                            const zScoreObj = channel.z_scores[featureName];
                                                            if (zScoreObj) {
                                                                // Add to channel values
                                                                channelValues.push({
                                                                    patient_id: patient.patient_id,
                                                                    channel: channel.channel,
                                                                    value: zScoreObj.raw_value
                                                                });

                                                                // Add to z-scores
                                                                zScores.push({
                                                                    patient_id: patient.patient_id,
                                                                    channel: channel.channel,
                                                                    z_score: zScoreObj.z_score
                                                                });
                                                            }
                                                        });

                                                        // Add the channel values and z-scores to the model for the modal
                                                        model.channelValues = channelValues;
                                                        model.zScores = zScores;

                                                        return (
                                                            <div
                                                                key={`${patient.patient_id}-${region.region_name}-${featureName}`}
                                                                className="normative-feature-card"
                                                                onClick={() => openFeatureModal(model, showZScores)}
                                                            >
                                                                <h4 className="normative-feature-heading" title={getFeatureDefinition(featureName).description}>
                                                                    <span className="feature-card-title-boxed">
                                                                        {formatFeatureLabel(featureName)}
                                                                    </span>
                                                                </h4>
                                                                <div className="normative-stats">
                                                                    <div className="stat">
                                                                        <span className="stat-label">Mean:</span>
                                                                        <span className="stat-value">
                                                                            {featureModel.mean.toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="stat">
                                                                        <span className="stat-label">Std:</span>
                                                                        <span className="stat-value">
                                                                            {featureModel.std.toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="normative-stats">
                                                                    <div className="stat">
                                                                        <span className="stat-label">Channels:</span>
                                                                        <span className="stat-value">
                                                                            {channelValues.length}
                                                                        </span>
                                                                    </div>
                                                                    <div className="stat">
                                                                        <span className="stat-label">Display:</span>
                                                                        <span className="stat-value z-score-value">
                                                                            {showZScores ? "Z-Scores" : "Raw Values"}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <DistributionPlot
                                                                    model={model}
                                                                    channelValues={channelValues}
                                                                    channelValuesLoading={false}
                                                                    zScores={zScores}
                                                                    zScoresLoading={false}
                                                                    showZScores={showZScores}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ))}

            {selectedModel && (
                <FeatureCardModal
                    isOpen={!!selectedModel}
                    onClose={closeFeatureModal}
                    model={selectedModel}
                    channelValues={selectedModel.channelValues || []}
                    channelValuesLoading={false}
                    zScores={selectedModel.zScores || []}
                    zScoresLoading={false}
                    showZScores={currentModalShowZScores}
                />
            )}
        </div>
    );
};
